import React from 'react';
import { Nullable } from 'global';
import makeStyles from '@mui/styles/makeStyles';
import { FormProvider, useForm } from 'react-hook-form';
import { Box, LinearProgress, Typography } from '@mui/material';
import { CardContainer } from 'components/shared/CardContainer/CardContainer';
import { RenderWithFallback } from 'components/shared/Conditional/RenderWithFallback';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';
import { CreditLineRequest, CreditLineStatusEnum } from '@trnsact/trnsact-shared-types';

import { useVO } from '../../hooks';
import { CreditLineFormValues } from '../model';
import { CreditLineForm } from './CreditLineForm';

interface Props {
  userName: string;
  isCreditLineRequested: boolean;
  creditLineRequest: Nullable<CreditLineRequest>;
}

export const CreditLineCard = ({ userName, isCreditLineRequested, creditLineRequest }: Props) => {
  const classes = useStyles();

  const form = useForm<CreditLineFormValues>({
    defaultValues: {
      notes: '',
      approvedBy: null,
      approvedOn: null,
      allNotes: creditLineRequest?.notes || '',
      requestedAmount: creditLineRequest?.requestedAmount || 0,
      approvedAmount: creditLineRequest?.approvedAmount || null,
      lenderProfileId: creditLineRequest?.lenderProfileId || null,
      status: creditLineRequest?.status || CreditLineStatusEnum.New,
      expirationDate: creditLineRequest?.expirationDate ? new Date(creditLineRequest.expirationDate) : null,
    },
  });

  const { updateCreditLineRequest, isUpdateCreditLineLoading } = useVO();

  return (
    <CardContainer title="Credit Line">
      <RenderWithFallback
        isLoading={isUpdateCreditLineLoading}
        fallBack={
          <Box>
            <Typography>Updating Credit Line...</Typography>
            <LinearProgress style={{ width: '100%' }} />
          </Box>
        }
      >
        <RenderWithCondition condition={isCreditLineRequested} fallBack={<Typography>Not Requested</Typography>}>
          <form className={classes.cardContainer}>
            <FormProvider {...form}>
              <CreditLineForm
                userName={userName}
                status={form.getValues('status')}
                onCreditLineUpdate={updateCreditLineRequest}
                requestedAmount={form.getValues('requestedAmount')}
                creditLineRequest={creditLineRequest ? creditLineRequest : undefined}
              />
            </FormProvider>
          </form>
        </RenderWithCondition>
      </RenderWithFallback>
    </CardContainer>
  );
};

const useStyles = makeStyles({
  cardContainer: {
    gap: '1rem',
    display: 'flex',
    flexDirection: 'column',

    '& .lineSection': {
      gap: '1rem',
      display: 'flex',
      flexDirection: 'column',

      '& .form': {
        gap: '1rem',
        display: 'flex',
        flexDirection: 'column',
      },
    },

    '& .notesSection': {
      gap: '1rem',
      display: 'flex',
      flexDirection: 'column',

      '& .customNote': {
        gap: '1rem',
        display: 'flex',
        alignItems: 'flex-end',

        '& > .addNotesBtn': {
          minWidth: 120,
        },
      },
    },
  },
});
